import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

// ---------- CREATE PRACTICE ----------

const createPracticeRequest = async (
  props: paths['/practice']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/practice']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/practice']['post']['responses']['201']['content']['application/json']
    >
  >(`/practice`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreatePracticeMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['practice'],
    mutationFn: (
      props: paths['/practice']['post']['requestBody']['content']['application/json'],
    ) => createPracticeRequest(props),
    onSuccess: () => client.invalidateQueries({ queryKey: ['practices'] }),
  });

  return mutation;
};

// ---------- EDIT PRACTICE ----------

const editPracticeRequest = async (
  props: paths['/practice/{record_id}']['patch']['requestBody']['content']['application/json'] & {
    practice_id: string;
  },
) => {
  const response = axiosInstance.patch<
    paths['/practice/{record_id}']['patch']['requestBody']['content']['application/json']
  >(`/practice/${props.practice_id}`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useEditPracticeMutation = (practice_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editPractice'],
    mutationFn: (
      props: paths['/practice/{record_id}']['patch']['requestBody']['content']['application/json'] & {
        practice_id: string;
      },
    ) => editPracticeRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['practices'] }),
        client.invalidateQueries({
          queryKey: ['practice', practice_id],
        }),
      ]),
  });

  return mutation;
};

// ---------- UPLOAD PRACTICE LOGO ----------

const uploadPracticeLogoRequest = async (props: {
  id: string;
  file: FormData;
}) => {
  const response = axiosInstance.put<{
    id: string;
    file: FormData;
  }>(`/practice/${props.id}/logo`, props.file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  const data = await response;

  return data;
};

export const useUploadPracticeLogoMutation = (practice_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['practiceLogoUpload'],
    mutationFn: (props: { id: string; file: FormData }) =>
      uploadPracticeLogoRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['practices'] }),
        client.invalidateQueries({
          queryKey: ['practice', practice_id, 'logo'],
        }),
      ]),
  });

  return mutation;
};

// ---------- DELETE PRACTICE LOGO ----------

const deletePracticeLogoRequest = async (props: { id: string }) => {
  const response = axiosInstance.delete<{
    id: string;
  }>(`/practice/${props.id}/logo`);
  const data = await response;

  return data;
};

export const useDeletePracticeLogoMutation = (practice_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deletePracticeLogo'],
    mutationFn: (props: { id: string }) => deletePracticeLogoRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['practices'] }),
        client.invalidateQueries({
          queryKey: ['practice', practice_id, 'logo'],
        }),
      ]),
  });

  return mutation;
};

// ---------- DOWNLOAD PRACTICES CSV ----------

export const practicesCsvDownloadRequest = async (
  props: paths['/record/practices/csv']['get']['parameters']['query'],
) => {
  const response = await axiosInstance.get(`/record/practices/csv`, {
    headers: {
      'Content-type': 'application/xml',
    },
    responseType: 'blob',
    params: props,
  });

  const data = (await response).data;

  return data;
};

export const usePracticesCsvDownloadQuery = () => {
  const mutation = useMutation({
    mutationKey: ['practicesCSVDownload'],
    mutationFn: (
      props: paths['/record/practices/csv']['get']['parameters']['query'],
    ) => practicesCsvDownloadRequest(props),
  });

  return mutation;
};

// ---------- DOWNLOAD PRACTICE CSV TEMPLATE ----------

export const practiceCsvTemplateDownloadRequest = async (record_id: string) => {
  const response = await axiosInstance.get(`/practice/template.csv`, {
    headers: {
      'Content-type': 'application/xml',
    },
    responseType: 'blob',
    params: {
      ...(record_id && { record_id }),
    },
  });

  const data = (await response).data;

  return data;
};

export const usePracticeCsvTemplateDownloadMutation = () => {
  const mutation = useMutation({
    mutationKey: ['practiceCSVTemplateDownload'],
    mutationFn: (record_id: string) =>
      practiceCsvTemplateDownloadRequest(record_id),
  });

  return mutation;
};

// ---------- IMPORT PRACTICES CSV ----------

export const practiceCsvImportRequest = async (
  record_id: string,
  file: FormData,
) => {
  const response = await axiosInstance.post(
    `/partner/${record_id}/practices/csv`,
    file,
    {
      headers: {
        'Content-type': 'application/xml',
      },
    },
  );

  const data = (await response).data;

  return data;
};

export const usePracticesCsvImportMutation = () => {
  const mutation = useMutation({
    mutationKey: ['practiceCSVImport'],
    mutationFn: ({ file, record_id }: { file: FormData; record_id: string }) =>
      practiceCsvImportRequest(record_id, file),
  });

  return mutation;
};
